import * as React from 'react'
import clx from 'classnames'
import { useState } from 'react'

import { StatCard } from './StatCard'
import { Card } from '@/app/features/common/Card'
import { MultiLayerDonutChart } from '@/app/features/dashboard/MultiLayerDonutChart'

import { useClickOutside } from '@/app/hooks/useClickOutside'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { SkeletonBox } from '@/app/features/common/SkeletonBox'
import { Loader } from '@/ui'
import { DashboardData } from '../types'

type Props = {
  stats?: DashboardData['driverWorkTime'] | null
  className?: string
  isLoading?: boolean
  isRefreshing?: boolean
}

const DriversHoursSkeleton = () => (
  <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-between tw-gap-6 tw-pt-1">
    <div className="tw-flex tw-w-full tw-flex-1 tw-gap-4 tw-px-6">
      {[0, 1, 2].map((column) => (
        <div key={column} className="tw-flex tw-w-full tw-flex-col tw-gap-2">
          <SkeletonBox className="tw-h-3 tw-w-20" />
          <SkeletonBox className="tw-h-1 tw-w-full tw-rounded-none" />
          <SkeletonBox className="tw-h-3 tw-w-16" />
          <SkeletonBox className="tw-h-4 tw-w-12" />
        </div>
      ))}
    </div>
    <div className="tw-flex tw-w-full tw-items-center tw-justify-center tw-pb-8">
      <SkeletonBox className="tw-h-[204px] tw-w-[204px] tw-rounded-full" />
    </div>
  </div>
)

export const DriversHoursCard = ({
  stats,
  className,
  isLoading,
  isRefreshing,
}: Props) => {
  const { t } = useLocalize()
  const [activeDriversTooltip, setActiveDriversTooltip] = useState<
    DashboardData['driverWorkTime']['driving'] | null
  >(null)

  const doughnutRef = React.useRef<HTMLDivElement>(null)
  useClickOutside({
    ref: doughnutRef,
    handler: () => setActiveDriversTooltip(null),
  })

  const onHover = (index: number | null) => {
    if (index === null) {
      setActiveDriversTooltip(null)
      return
    }
    const name = ['driving', 'otherWork', 'duty'][index]
    setActiveDriversTooltip(stats?.[name])
  }

  const hasWorkTimeBreakdown =
    Number(stats?.driving?.hours ?? 0) > 0 ||
    Number(stats?.otherWork?.hours ?? 0) > 0 ||
    Number(stats?.duty?.hours ?? 0) > 0

  return (
    <Card
      title={t('dashboard.drivers-work-time')}
      classNameContent="tw-h-full !tw-px-0 !tw-pb-0"
      className={clx('tw-relative tw-bg-primary-linear', className)}
    >
      {isRefreshing && (
        <Loader
          position="absolute"
          className="tw-inset-0 tw-z-10 tw-bg-base-100/60 tw-backdrop-blur-[1px]"
          size="sm"
        />
      )}
      {isLoading ? (
        <DriversHoursSkeleton />
      ) : (
        <div
          className={clx(
            'tw-flex tw-h-full tw-flex-col tw-items-center tw-gap-6 tw-pt-1',
            {
              'tw-justify-center': !hasWorkTimeBreakdown,
              'tw-justify-between': hasWorkTimeBreakdown,
            },
          )}
        >
          <div className="tw-flex tw-w-full tw-flex-1 tw-gap-4 tw-px-6">
            {stats?.driving && (
              <StatCard
                label={t('dashboard.driving')}
                hours={hasWorkTimeBreakdown ? stats.driving?.hours || 0 : 0}
                percent={hasWorkTimeBreakdown ? stats.driving?.percent || 0 : 0}
                barColor={hasWorkTimeBreakdown ? '#2448BB' : '#cccccc'}
              />
            )}
            {stats?.otherWork && (
              <StatCard
                label={t('dashboard.other-work')}
                hours={hasWorkTimeBreakdown ? stats.otherWork?.hours || 0 : 0}
                percent={
                  hasWorkTimeBreakdown ? stats.otherWork?.percent || 0 : 0
                }
                barColor={
                  hasWorkTimeBreakdown
                    ? 'linear-gradient(90deg, #2C57FF 0%, #48A1FF 100%)'
                    : '#cccccc'
                }
              />
            )}
            {stats?.duty && (
              <StatCard
                label={t('dashboard.duty')}
                hours={hasWorkTimeBreakdown ? stats.duty?.hours || 0 : 0}
                percent={hasWorkTimeBreakdown ? stats.duty?.percent || 0 : 0}
                barColor={hasWorkTimeBreakdown ? '#52BEFF' : '#cccccc'}
              />
            )}
          </div>

          <div className="tw-relative tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-between tw-pb-8">
            {activeDriversTooltip && (
              <div className="tw-pointer-events-none tw-absolute tw-z-10 tw-flex tw-max-w-80 tw-flex-col tw-gap-0.5 tw-rounded tw-bg-content-base/80 tw-px-2 tw-py-1 tw-text-sm tw-text-secondary">
                <div className="tw-flex tw-justify-between tw-gap-3">
                  <span className="tw-flex tw-gap-1">
                    <p className="tw-mb-0">Min</p>
                    <p className="tw-mb-0">
                      {activeDriversTooltip?.min?.driver}
                    </p>
                  </span>
                  <p className="tw-mb-0">
                    {activeDriversTooltip?.min?.hours.toLocaleString('pl-PL')}h
                  </p>
                </div>
                <div className="tw-flex tw-justify-between tw-gap-3">
                  <span className="tw-flex tw-gap-1">
                    <p className="tw-mb-0">Max</p>
                    <p className="tw-mb-0">
                      {' '}
                      {activeDriversTooltip?.max?.driver}
                    </p>
                  </span>
                  <p className="tw-mb-0">
                    {activeDriversTooltip?.max?.hours.toLocaleString('pl-PL')}h
                  </p>
                </div>
              </div>
            )}
            <MultiLayerDonutChart
              doughnutRef={doughnutRef}
              values={[
                Number(stats?.driving?.hours ?? 0),
                Number(stats?.otherWork?.hours ?? 0),
                Number(stats?.duty?.hours ?? 0),
              ]}
              onDoughnutHover={onHover}
            />
          </div>
        </div>
      )}
    </Card>
  )
}
